<template>
    <div :class="{ 'invalid': isInvalid }">
        <multiselect
            v-model="current"
            :options="data"
            :multiple="multiple"
            :closeOnSelect="!multiple"
            :clearOnSelect="false"
            :preserveSearch="false"
            :placeholder="placeholder"
            :selectLabel="multiple ? $t('custom.multiselect.selectLabel') : null"
            :selectedLabel="multiple ? $t('custom.multiselect.selectedLabel') : null"
            :deselectLabel="multiple ? $t('custom.multiselect.deselectLabel') : null"
            :label="label"
            :trackBy="keyId"
            :searchable="searchable"
            :preselectFirst="preselectFirst"
            :allowEmpty="!noAllowEmpty"
            :disabled="disabled"
            @input="onInput"
            @select="select"
            @open="(lazy && data.length === 0) || getOptionsOnOpen ? clearAndSetData() : $event"
        >
            <template
                v-if="hasOptionSlot"
                slot="option"
                slot-scope="{ option }"
            >
                <slot
                    name="option"
                    :option="option"
                />
            </template>
            <template
                slot="selection"
                slot-scope="{ values, isOpen }"
            >
                <span
                    v-show="values.length && !isOpen"
                    class="multiselect__single"
                >
                    {{ selectedText }}: {{ values.length }}
                </span>
            </template>
            <template slot="noOptions">
                <span>
                    <b-spinner
                        v-if="loading"
                        small
                    />
                    <span v-else>{{ $t('custom.multiselect.noOptions') }}</span>
                </span>
            </template>
            <span slot="noResult">{{ $t('custom.multiselect.noResult') }}</span>
        </multiselect>
        <label
            v-show="isInvalid"
            class="invalid-feedback mt-2"
        >
            {{ $t('custom.multiselect.invalid') }}
        </label>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
    name: 'CustomMultiselect',
    components: { Multiselect },
    props: {
        value: {
            type: [Array, String, Number],
            'default': null,
        },

        options: {
            type: Array,
            'default': function() {
                return [];
            },
        },

        placeholder: {
            type: String,
            'default': '',
        },

        selectedText: {
            type: String,
            'default': 'Выбрано',
        },

        keyId: {
            type: String,
            'default': 'id',
        },

        label: {
            type: String,
            'default': 'description',
        },

        src: {
            type: String,
            'default': '',
        },

        multiple: {
            type: Boolean,
            'default': true,
        },

        searchable: {
            type: Boolean,
            'default': false,
        },

        isInvalid: {
            type: Boolean,
            'default': false,
        },

        noAllowEmpty: {
            type: Boolean,
            'default': false,
        },

        lazy: {
            type: Boolean,
            'default': false,
        },

        disabled: {
            type: Boolean,
            'default': false,
        },

        preselectFirst: {
            type: Boolean,
            'default': true,
        },

        getOptionsOnOpen: {
            type: Boolean,
            'default': false,
        },

        hasOptionSlot: {
            type: Boolean,
            'default': false,
        },
    },

    data() {
        return {
            current: [],
            data: this.options,
            loading: false,
        };
    },

    methods: {
        getOptions: function() {
            if (this.lazy) {
                if (this.data.length !== 0) {
                    return this.data;
                }
                if (this.value) {
                    this.current.push({});
                    this.current[0][this.keyId] = this.value;
                }
                return;
            }
            if (this.src === '') {
                return this.data;
            }
            this.setData();
        },

        clearAndSetData() {
            this.data = [];
            this.current = this.getCurrent();
            this.setData();
        },

        setData: function() {
            this.loading = true;
            return this.axios.get(this.src)
                .then((response) => {
                    this.data = response.data;
                    this.current = this.getCurrent();
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        },

        getCurrent: function() {
            const that = this;
            const current = [];
            if (that.value == null) {
                return null;
            }
            const values = that.value instanceof Array ? that.value : [that.value];
            for (const value of values) {
                const item = that.data.find(x => x[that.keyId] === value);
                if (item != null) {
                    current.push(item);
                }
            }
            return current;
        },

        onInput: function() {
            this.$emit(
                'input',
                this.current != null ?
                    (this.multiple ? this.current.map(el => el[this.keyId]) : this.current[this.keyId]) :
                    null,
            );
        },

        select(item) {
            this.$emit('select', item);
        },
    },

    watch: {
        value: function() {
            this.current = this.getCurrent();
        },

        data: function() {
            this.$emit('update:options', this.data);
        },

        options: function() {
            this.data = this.options;
            this.current = this.getCurrent();
        },

        src: function() {
            this.getOptions();
        },
    },

    mounted() {
        this.getOptions();
    },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
    .multiselect {
        margin-bottom: 0;
        min-height: auto;

        & .multiselect__ {
            &input, &single {
                font-size: 13px;
            }

            &content-wrapper {
                font-size: 13px;
            }

            &select {
                top: 5px;
            }

            &tags {
                padding-left: 12px;
                min-height: 3rem;
                padding-top: 1.1em;
                font-size: 13px;

                & .multiselect__ {
                    &placeholder {
                        color: #6c757d;
                        font-size: 13px;
                        margin-bottom: 0;
                        padding-top: 0;
                    }
                }
            }
        }
    }

    .invalid-feedback {
        display: block;
    }

    .invalid .multiselect__tags {
        border-color: #dc3545 !important;
    }
</style>